import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { useExam } from "@/context/ExamContext";
import { useWhiteLabel } from "@/contexts/WhiteLabelContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Link } from "react-router-dom";
import { 
  CheckCircle, Clock, Sparkles, Shield, ArrowRight, 
  Award, Zap, Brain, Target, TrendingUp, Lock, 
  AlertTriangle, BarChart3, BookOpen, Loader2, ExternalLink, Gift
} from "lucide-react";

const UpgradePage = () => {
  const { user, setUser } = useAuth();
  const { isSI } = useExam();
  const [searchParams] = useSearchParams();
  // Honor an explicit `?exam=si` intent (from the SI upgrade CTA) so the SI plan
  // loads deterministically even if the exam context hasn't hydrated yet.
  const wantsSI = isSI || searchParams.get("exam") === "si";
  // Exam-scoped entitlement. In SI context, only an "SI" entitlement counts as
  // active PRO (global UPSC PRO does NOT unlock SI, and vice-versa).
  const proExamsList = user?.pro_exams || [];
  const hasProForContext = user?.role === "ADMIN" || user?.is_admin || (wantsSI ? proExamsList.includes("SI") : user?.role === "PRO");
  // SI subscription window (rolling; renewals stack leftover days on top).
  const siEndDate = user?.si_subscription_end ? new Date(user.si_subscription_end) : null;
  const siDaysRemaining = siEndDate ? Math.ceil((siEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
  const siEndLabel = siEndDate ? siEndDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const navigate = useNavigate();
  const { isWhiteLabel, shouldShowPayment, config, getInstituteName } = useWhiteLabel();
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [razorpayKey, setRazorpayKey] = useState("");
  const [proPlans, setProPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(wantsSI ? "clarias_si_yearly" : "clarias_pro_yearly");
  const [subscription, setSubscription] = useState(null);
  const { toast } = useToast();
  
  // Coupon code state
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  // Referral code state (separate from coupon — attributes the referrer, no discount)
  const [referralCode, setReferralCode] = useState("");
  const [referralApplied, setReferralApplied] = useState(false);
  const [applyingReferral, setApplyingReferral] = useState(false);

  // Redirect white-label users who shouldn't see core payment
  useEffect(() => {
    if (isWhiteLabel && !shouldShowPayment()) {
      // Redirect to dashboard for white-label without payment access
      toast({
        title: "Already PRO!",
        description: `Your ${getInstituteName()} membership includes full access.`,
      });
      navigate("/dashboard");
    }
  }, [isWhiteLabel, shouldShowPayment, navigate, toast, getInstituteName]);

  // Load Razorpay key + PRO tier products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/razorpay/products`);
        setRazorpayKey(data.key_id);
        if (wantsSI) {
          // SI aspirants get the single, isolated SI PRO plan.
          const si = data.si_products || [];
          setProPlans(si);
          if (si.length) setSelectedPlanId(si[0].id);
        } else {
          // Filter to only the 3 PRO tiers (monthly/quarterly/yearly)
          const proTierIds = ["clarias_pro_monthly", "clarias_pro_quarterly", "clarias_pro_yearly"];
          const tiers = (data.products || []).filter(p => proTierIds.includes(p.id));
          setProPlans(tiers);
        }
      } catch (err) {
        console.error("Failed to load Razorpay products:", err);
      }
    };
    const loadSubscription = async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/payments/subscription`);
        setSubscription(data);
      } catch (err) {
        setSubscription(null);
      }
    };
    loadProducts();
    loadSubscription();
  }, [wantsSI]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Validate coupon code
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({ variant: "destructive", description: "Please enter a coupon code" });
      return;
    }
    
    setValidatingCoupon(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/razorpay/validate-coupon`, {
        coupon_code: couponCode.trim(),
        product_id: selectedPlanId
      });
      
      if (data.valid) {
        setCouponApplied(data);
        toast({ description: `🎉 ${data.message} You save ${data.display_discount}!` });
      } else {
        toast({ variant: "destructive", description: data.message });
        setCouponApplied(null);
      }
    } catch (err) {
      toast({ variant: "destructive", description: err.response?.data?.detail || "Invalid coupon code" });
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  };
  
  // Remove coupon
  const handleRemoveCoupon = () => {
    setCouponCode("");
    setCouponApplied(null);
  };

  // Apply referral code — attributes the referrer (no discount to buyer)
  const handleApplyReferral = async () => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      toast({ variant: "destructive", description: "Please enter a referral code" });
      return;
    }
    setApplyingReferral(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/referrals/attribute`, { code });
      if (data.ok) {
        setReferralApplied(true);
        toast({ description: "🎁 Referral applied! Your friend earns credits once your purchase completes." });
      } else if (data.reason === "already_attributed") {
        setReferralApplied(true);
        toast({ description: "You already have a referrer linked to your account." });
      } else {
        toast({ variant: "destructive", description: "Could not apply referral code." });
      }
    } catch (err) {
      toast({ variant: "destructive", description: err.response?.data?.detail || "Invalid referral code" });
    } finally {
      setApplyingReferral(false);
    }
  };

  const handlePayment = async () => {
    // CRITICAL: User must agree to terms before payment
    if (!agreedToTerms) {
      setShowTermsError(true);
      toast({ 
        variant: "destructive", 
        description: "Please agree to the Terms of Service, Privacy Policy, and Refund Policy before proceeding." 
      });
      return;
    }

    setLoading(true);
    
    try {
      // Step 1: Create order (with coupon if applied)
      const { data: orderData } = await managedAxios.post(`${API_BASE}/razorpay/create-order`, {
        product_id: selectedPlanId,
        coupon_code: couponApplied ? couponApplied.coupon_code : null
      });

      // 🎁 FREE-GRANT PATH (100% off coupon like BIRTHDAY100):
      // Backend already granted access — skip Razorpay checkout entirely.
      if (orderData.free_grant && orderData.access_granted) {
        const updatedUser = { ...user, role: "PRO" };
        setUser(updatedUser);
        const stored = localStorage.getItem("ranker-auth");
        if (stored) {
          const parsed = JSON.parse(stored);
          localStorage.setItem("ranker-auth", JSON.stringify({ ...parsed, user: updatedUser }));
        }
        toast({
          description: `🎂 ${orderData.coupon_applied} applied — 100% off! Welcome to CLARIAS PRO.`,
          duration: 6000,
        });
        setTimeout(() => { window.location.href = "/dashboard"; }, 1800);
        return;
      }

      // Step 2: Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "CLARIAS",
        description: orderData.product.name,
        order_id: orderData.order_id,
        handler: async function (response) {
          // Step 3: Verify payment on backend
          try {
            const { data: verifyData } = await managedAxios.post(`${API_BASE}/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyData.status === "success" && verifyData.access_granted) {
              // Update local user state
              const updatedUser = { ...user, role: "PRO" };
              setUser(updatedUser);
              
              // Update localStorage
              const stored = localStorage.getItem("ranker-auth");
              if (stored) {
                const parsed = JSON.parse(stored);
                localStorage.setItem("ranker-auth", JSON.stringify({ ...parsed, user: updatedUser }));
              }
              
              toast({ 
                description: "🎉 Welcome to CLARIAS PRO! Your access has been activated.",
                duration: 5000
              });
              
              // Redirect to dashboard after success
              setTimeout(() => {
                window.location.href = "/dashboard";
              }, 2000);
            } else if (verifyData.status === "pending") {
              toast({
                description: "Payment is being verified. Please do not retry. This usually takes a few minutes.",
                duration: 8000
              });
            }
          } catch (err) {
            console.error("Payment verification failed:", err);
            toast({ 
              variant: "destructive", 
              description: "Payment verification failed. If amount was debited, please contact support@clarias.in" 
            });
          }
        },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        notes: {
          user_id: user?.id || "",
          product: selectedPlanId
        },
        theme: {
          color: "#4F46E5" // Indigo
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast({
              description: "Payment cancelled. You can try again anytime.",
            });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response) {
        setLoading(false);
        toast({
          variant: "destructive",
          description: "Payment failed. If any amount was debited, it will be automatically reversed by your bank within 3–5 working days."
        });
      });
      
      razorpay.open();
      
    } catch (err) {
      console.error("Payment initiation failed:", err);
      const errorMessage = err.response?.data?.detail || "Could not initiate payment. Please try again.";
      toast({ 
        variant: "destructive", 
        description: errorMessage
      });
      setLoading(false);
    }
  };

  // Derive currently selected plan from fetched proPlans
  const selectedPlan = proPlans.find(p => p.id === selectedPlanId);

  // Display data for the main pricing card — prefers backend data, falls back to defaults
  const product = selectedPlan ? {
    name: selectedPlan.name,
    originalPrice: selectedPlan.display_original,
    price: selectedPlan.display_amount,
    period: selectedPlan.duration_days === 30 ? "/month"
          : selectedPlan.duration_days === 90 ? "/quarter"
          : (wantsSI ? "/year" : "/till Mains 2027"),
    validity: selectedPlan.validity,
    discount: `Save ₹${((selectedPlan.original_amount - selectedPlan.amount) / 100).toLocaleString('en-IN')} (${selectedPlan.discount_percent}% OFF)`,
    features: selectedPlan.features
  } : (wantsSI ? {
    // Fallback (network failure) — SI Yearly defaults
    name: "CLARIAS SI PRO — Yearly",
    originalPrice: "₹10,000",
    price: "₹3,999",
    period: "/year",
    validity: "Valid 1 year from purchase",
    discount: "Save ₹6,001 (60% OFF)",
    features: [
      "SI Mains Test Series (200-Q · 180-min papers)",
      "Unlimited SI Aptitude Practice (Arithmetic / Reasoning / DI)",
      "GS Topic Heatmap (Telangana & Andhra Pradesh)",
      "CLARIAS Behaviour Analytics on every paper",
      "Full solution review & explanations",
      "Priority Support"
    ]
  } : {
    // Fallback (network failure) — Founding Yearly defaults
    name: "CLARIAS PRO — Founding Yearly",
    originalPrice: "₹23,999",
    price: "₹6,999",
    period: "/till Mains 2027",
    validity: "Valid till Mains 2027 (covers 2 attempts)",
    discount: "Save ₹17,000 (71% OFF)",
    features: [
      "Unlimited Deep-Study Master Material (150+ chapters, all subjects)",
      "Unlimited Chapter & Topic Practice (all subjects)",
      "Unlimited Handwritten & Typed Answer Evaluation",
      "Rubric-graded Answer Writing & Evaluation (GS1–4 + Essay)",
      "Essay Engine — Prediction, Writing & Evaluation",
      "Full Current Affairs — Daily Intelligence (Prelims + Mains)",
      "Unlimited CSAT Practice Suite",
      "Full PYQ Access + Pattern Analysis",
      "Interactive Mind Maps & Revision Panels",
      "Core Intelligence Engine",
      "Selection Probability Calculator",
      "Clarity Assistant — Personal Study Planner",
      "Weak-Area Analytics & Mistake Tracker",
      "Topper Benchmark Comparison",
      "Priority Support"
    ]
  });

  // FREE vs PRO comparison
  const freeVsPro = wantsSI ? [
    { feature: "GS Practice MCQs", free: "1 set/day", pro: "Unlimited (all chapters)" },
    { feature: "Aptitude Practice", free: "1 set/day", pro: "Unlimited (Arithmetic/Reasoning/DI)" },
    { feature: "SI Mains Test Series", free: "Locked", pro: "Full 200-Q · 180-min papers" },
    { feature: "Detailed Solutions", free: "Limited", pro: "Full step-by-step solutions" },
    { feature: "GS Topic Heatmap", free: "Sample", pro: "Telangana & Andhra Pradesh" },
    { feature: "Daily Current Affairs", free: "Limited", pro: "Full state + national feed" },
    { feature: "Behaviour Analytics", free: "❌", pro: "✓ Every paper" },
    { feature: "Support", free: "Standard", pro: "Priority Support" },
  ] : [
    { feature: "MCQ Generation", free: "Limited", pro: "Unlimited (All GS)" },
    { feature: "Answer Writing (GS1-4)", free: "1 full/suite, then partial", pro: "Unlimited Full Answers" },
    { feature: "Handwritten Evaluation", free: "1 full, then partial", pro: "Unlimited Full Evaluations" },
    { feature: "Essay Writing", free: "1 full, then partial", pro: "Unlimited Full Essays" },
    { feature: "Essay Evaluation", free: "1 full, then partial", pro: "Unlimited Full Evaluations" },
    { feature: "PYQ Analysis", free: "Sample", pro: "Full Patterns & Trends" },
    { feature: "Daily Intelligence", free: "Limited", pro: "Full Exam-Focused CA" },
    { feature: "Selection Probability", free: "❌", pro: "✓ Data-Driven" },
    { feature: "Clarity AI Planner", free: "❌", pro: "✓ Personalized" },
    { feature: "Test Series", free: "Sample", pro: "Premium Full Access" },
    { feature: "Support", free: "Standard", pro: "Priority Support" },
  ];

  // PRO benefits
  const proBenefits = wantsSI ? [
    { icon: Brain, title: "SI Mains Test Series", desc: "Full 200-Q · 180-min mock papers" },
    { icon: Target, title: "Unlimited Aptitude Practice", desc: "Arithmetic, Reasoning & Data Interpretation" },
    { icon: BarChart3, title: "GS Topic Heatmap", desc: "Telangana & Andhra Pradesh coverage" },
    { icon: Shield, title: "Behaviour Analytics", desc: "CLARIAS analysis on every paper" },
    { icon: Clock, title: "Full Solutions", desc: "Step-by-step explanations for every question" },
    { icon: Zap, title: "Daily Current Affairs", desc: "State + national exam-focused feed" },
  ] : [
    { icon: Brain, title: "AI-Powered Answer Writing", desc: "Generate model answers for GS1-4 papers" },
    { icon: Target, title: "Selection Probability", desc: "Know your realistic chances based on data" },
    { icon: Clock, title: "Clarity AI Planner", desc: "Personalized daily study schedule" },
    { icon: Shield, title: "Handwritten Evaluation", desc: "Get your handwritten answers evaluated by AI" },
    { icon: BarChart3, title: "Full PYQ Analysis", desc: "Complete patterns and trends analysis" },
    { icon: Zap, title: "Essay Suite", desc: "Topic prediction and AI writing assistance" },
  ];

  return (
    <div className="min-h-screen bg-slate-50/30" data-testid="upgrade-page">
      {/* Premium Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100/50 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 py-16 space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 bg-indigo-50 rounded-full px-4 py-2">
            <Sparkles className="w-4 h-4 text-indigo-500" />
            <span className="text-sm text-indigo-700 font-medium">Decision Intelligence Platform</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900">
            Upgrade to <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">{wantsSI ? "CLARIAS SI PRO" : "CLARIAS PRO"}</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            {wantsSI
              ? "Complete Sub-Inspector prep — SI Mains test series, unlimited practice & analytics"
              : "Complete UPSC preparation suite — rubric-graded, examiner-trained"}
          </p>
        </div>

        {/* Earn-it-free referral strip (esp. for FREE users hesitating on price) */}
        <div
          className="flex items-center gap-3 justify-center flex-wrap bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3.5 cursor-pointer hover:bg-amber-100/70 transition-colors -mt-6"
          onClick={() => navigate("/refer")}
          data-testid="upgrade-refer-cta"
        >
          <Gift className="w-5 h-5 text-amber-600 shrink-0" />
          <span className="text-sm text-slate-700">
            Not ready to pay? <span className="font-semibold text-amber-700">Earn PRO for free</span> — invite friends &amp; unlock 1 month for every 3 who go PRO.
          </span>
          <span className="text-sm font-semibold text-amber-700 inline-flex items-center gap-1">
            Refer &amp; Earn <ArrowRight className="w-4 h-4" />
          </span>
        </div>

        {/* SI PRO active — renewal banner (rolling plan; renew anytime, leftover days stack) */}
        {wantsSI && hasProForContext && (
          <Card className="bg-gradient-to-br from-sky-50 to-indigo-50 border-sky-200 shadow-sm" data-testid="si-renew-banner">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1">
                  <Badge className="bg-sky-500 text-white border-0">SI PRO • Active</Badge>
                  <p className="text-base font-semibold text-slate-900 mt-2">
                    {siDaysRemaining !== null
                      ? (siDaysRemaining > 0
                          ? <>You have <span className="text-sky-600">{siDaysRemaining}</span> day{siDaysRemaining === 1 ? '' : 's'} of SI access left.</>
                          : <>Your SI access has expired.</>)
                      : <>You have full CLARIAS SI PRO access.</>}
                  </p>
                  {siEndLabel && <p className="text-xs text-slate-500">Current expiry: {siEndLabel}</p>}
                </div>
                <CheckCircle className="w-6 h-6 text-emerald-500 mt-1" />
              </div>
              <div className="text-sm text-slate-600 bg-white/70 rounded-lg p-3 border border-slate-200">
                💡 <strong>Renew anytime.</strong> Any leftover days are automatically added on top of your new plan — you never lose what you've paid for.
              </div>
            </CardContent>
          </Card>
        )}

        {/* PRO user state — Yearly or Lifetime get a static banner. Monthly/Quarterly get a renewal flow. */}
        {!wantsSI && user?.role === "PRO" && (() => {
          const plan = subscription?.subscription_plan;
          const isLifetime = subscription?.is_lifetime;
          const daysRemaining = subscription?.days_remaining;
          const subEnd = subscription?.subscription_end
            ? new Date(subscription.subscription_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
            : null;
          const isRenewable = plan === "clarias_pro_monthly" || plan === "clarias_pro_quarterly";

          if (isLifetime) {
            return (
              <Card className="bg-gradient-to-r from-indigo-500 to-purple-500 border-0 text-white">
                <CardContent className="p-8 text-center">
                  <Award className="w-12 h-12 mx-auto mb-4 opacity-90" />
                  <Badge className="bg-white/20 text-white border-0 mb-3">PRO Lifetime</Badge>
                  <p className="text-lg">You have lifetime CLARIAS PRO access.</p>
                </CardContent>
              </Card>
            );
          }

          if (!isRenewable) {
            // Yearly user — nothing to renew till 2027
            return (
              <Card className="bg-gradient-to-r from-indigo-500 to-purple-500 border-0 text-white">
                <CardContent className="p-8 text-center">
                  <Award className="w-12 h-12 mx-auto mb-4 opacity-90" />
                  <Badge className="bg-white/20 text-white border-0 mb-3">PRO Active</Badge>
                  <p className="text-lg">You already have CLARIAS PRO access.</p>
                  {subEnd && (
                    <p className="text-sm opacity-90 mt-2">Valid till {subEnd} (covers Mains 2027)</p>
                  )}
                </CardContent>
              </Card>
            );
          }

          // Monthly / Quarterly user — show renewal banner + reuse the tier selector
          const planLabel = plan === "clarias_pro_monthly" ? "Monthly" : "Quarterly";
          return (
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200 shadow-sm" data-testid="pro-renew-banner">
              <CardContent className="p-6 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="space-y-1">
                    <Badge className="bg-indigo-500 text-white border-0">PRO • {planLabel}</Badge>
                    <p className="text-base font-semibold text-slate-900 mt-2">
                      {daysRemaining > 0
                        ? <>You have <span className="text-indigo-600">{daysRemaining}</span> day{daysRemaining === 1 ? '' : 's'} of access left.</>
                        : <>Your access has expired.</>}
                    </p>
                    {subEnd && <p className="text-xs text-slate-500">Current expiry: {subEnd}</p>}
                  </div>
                  <CheckCircle className="w-6 h-6 text-emerald-500 mt-1" />
                </div>
                <div className="text-sm text-slate-600 bg-white/70 rounded-lg p-3 border border-slate-200">
                  💡 <strong>Renew anytime.</strong> Any leftover days are automatically added on top of your new plan — you never lose what you've paid for.
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Plan Selector — SI: always shown (renewal stacks days). UPSC: FREE users + renewable PRO. */}
        {(wantsSI ||
          (!wantsSI && (user?.role !== "PRO" ||
           subscription?.subscription_plan === "clarias_pro_monthly" ||
           subscription?.subscription_plan === "clarias_pro_quarterly"))) && (
          <>
            {proPlans.length > 0 && (
              <div className={`grid gap-4 ${wantsSI ? "md:grid-cols-2 max-w-2xl mx-auto" : "md:grid-cols-3"}`} data-testid="pro-tier-selector">
                {proPlans.map((plan) => {
                  const isSelected = selectedPlanId === plan.id;
                  const isYearly = plan.id === "clarias_pro_yearly" || plan.id === "clarias_si_yearly";
                  const perMonth = plan.duration_days === 30
                    ? null
                    : plan.duration_days === 90
                      ? `≈ ₹${Math.round(plan.amount / 100 / 3).toLocaleString('en-IN')}/mo`
                      : `≈ ₹${Math.round(plan.amount / 100 / 12).toLocaleString('en-IN')}/mo`;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => {
                        setSelectedPlanId(plan.id);
                        // Reset coupon when plan changes (amounts differ)
                        setCouponCode("");
                        setCouponApplied(null);
                      }}
                      data-testid={`select-plan-${plan.id}`}
                      className={`relative text-left rounded-2xl border-2 p-5 transition-all ${
                        isSelected
                          ? "border-indigo-500 bg-indigo-50 shadow-lg ring-2 ring-indigo-200"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      {isYearly && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md whitespace-nowrap">
                          {plan.id === "clarias_si_yearly" ? "BEST VALUE" : "BEST VALUE — Founding"}
                        </span>
                      )}
                      <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                        {plan.duration_days === 30 ? "Monthly" : plan.duration_days === 90 ? "Quarterly" : (wantsSI ? "Yearly" : "Yearly (Founding)")}
                      </div>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-slate-900">{plan.display_amount}</span>
                        {plan.display_original && (
                          <span className="text-sm text-slate-400 line-through">{plan.display_original}</span>
                        )}
                      </div>
                      {perMonth && (
                        <div className="text-xs text-emerald-600 font-medium mt-1">{perMonth}</div>
                      )}
                      <div className="text-xs text-slate-500 mt-2">{plan.validity}</div>
                      {isSelected && (
                        <div className="absolute top-3 right-3">
                          <CheckCircle className="w-5 h-5 text-indigo-500" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <Card className="overflow-hidden border-2 border-indigo-500 shadow-xl">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="text-2xl font-bold">{product.name}</h2>
                    <p className="text-indigo-100">{product.validity}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg line-through opacity-60">{product.originalPrice}</span>
                    <span className="text-4xl font-bold">{product.price}</span>
                    <span className="text-indigo-100">{product.period}</span>
                  </div>
                  <Badge className="bg-emerald-500 text-white border-0 mt-2">
                    {product.discount}
                  </Badge>
                </div>
              </div>
            </div>
            
            <CardContent className="p-6 space-y-6">
              {/* Features */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">What&apos;s included:</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coupon Code Section */}
              <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-3">Have a coupon code?</h4>
                {!couponApplied ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm uppercase"
                      disabled={validatingCoupon}
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={validatingCoupon || !couponCode.trim()}
                      variant="outline"
                      className="px-4"
                    >
                      {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium text-emerald-700">{couponApplied.coupon_code}</span>
                        <Badge className="bg-emerald-100 text-emerald-700 border-0">
                          {couponApplied.discount_percent}% OFF
                        </Badge>
                      </div>
                      <Button
                        onClick={handleRemoveCoupon}
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-red-500"
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between text-slate-600">
                        <span>Original Price:</span>
                        <span className="line-through">{couponApplied.display_original}</span>
                      </div>
                      <div className="flex justify-between text-emerald-600 font-medium">
                        <span>Discount:</span>
                        <span>-{couponApplied.display_discount}</span>
                      </div>
                      <div className="flex justify-between text-slate-900 font-bold text-base pt-1 border-t">
                        <span>Final Price:</span>
                        <span>{couponApplied.display_final}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Referral Code Section — attributes referrer, no discount */}
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/60" data-testid="upgrade-referral-section">
                <h4 className="font-medium text-slate-900 mb-1 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-amber-600" /> Have a referral code?
                </h4>
                <p className="text-xs text-slate-500 mb-3">
                  Enter a friend's code — they'll earn 100 credits once your PRO purchase completes. (This is not a discount.)
                </p>
                {!referralApplied ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. CLRXXXXXX"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      className="flex-1 px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm uppercase bg-white"
                      disabled={applyingReferral}
                      data-testid="referral-code-input"
                    />
                    <Button
                      onClick={handleApplyReferral}
                      disabled={applyingReferral || !referralCode.trim()}
                      variant="outline"
                      className="px-4 border-amber-400 text-amber-700 hover:bg-amber-100"
                      data-testid="apply-referral-btn"
                    >
                      {applyingReferral ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="referral-applied">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700">Referral code applied</span>
                  </div>
                )}
              </div>

              {/* Legal Consent - MANDATORY */}
              <div className={`p-4 rounded-lg border-2 transition-colors ${
                showTermsError ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"
              }`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => {
                      setAgreedToTerms(checked);
                      if (checked) setShowTermsError(false);
                    }}
                    className="mt-1"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer">
                    I agree to the{" "}
                    <Link to="/terms" className="text-indigo-600 hover:underline" target="_blank">
                      Terms of Service
                    </Link>
                    ,{" "}
                    <Link to="/privacy" className="text-indigo-600 hover:underline" target="_blank">
                      Privacy Policy
                    </Link>
                    , and{" "}
                    <Link to="/refund" className="text-indigo-600 hover:underline" target="_blank">
                      Refund Policy
                    </Link>
                    .
                  </label>
                </div>
                {showTermsError && (
                  <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Please agree to the terms to proceed with payment
                  </p>
                )}
              </div>

              {/* Razorpay Payment Button */}
              <Button
                onClick={handlePayment}
                disabled={loading || !agreedToTerms}
                className="w-full py-6 text-lg rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing Payment...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Upgrade to {wantsSI ? "SI PRO" : "PRO"} - {couponApplied ? couponApplied.display_final : product.price}
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                🔒 Secure payment via Razorpay • {product.validity}
              </p>
            </CardContent>
          </Card>
          </>
        )}

        {/* FREE vs PRO Comparison */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-6">FREE vs PRO</h2>
          <Card className="overflow-hidden border-0 shadow-sm">
            <div className="grid grid-cols-3 bg-slate-100 p-4 font-semibold text-slate-700">
              <div>Feature</div>
              <div className="text-center">FREE</div>
              <div className="text-center text-indigo-600">PRO</div>
            </div>
            {freeVsPro.map((row, idx) => (
              <div key={idx} className={`grid grid-cols-3 p-4 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                <div className="text-slate-700">{row.feature}</div>
                <div className="text-center text-slate-500">{row.free}</div>
                <div className="text-center text-slate-900 font-medium">{row.pro}</div>
              </div>
            ))}
          </Card>
        </div>

        {/* PRO Benefits */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-6">What PRO Unlocks</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {proBenefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{benefit.title}</h3>
                        <p className="text-sm text-slate-600">{benefit.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Support Info */}
        <div className="text-center text-sm text-slate-500 space-y-2">
          <p>
            Questions about payment?{" "}
            <a href="mailto:support@clarias.in" className="text-indigo-600 hover:underline">
              support@clarias.in
            </a>
          </p>
          <p className="flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" />
            Your payment is secure and encrypted
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
